import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { queryClient } from '../hooks/queryClient'
import { updateGlobalSettings } from '../pages/globalSettings/api'
import { globalSettingsStorage } from '../utils/globalSettingsStorage'

import ar from './locales/ar.json'
import bn from './locales/bn.json'
import de from './locales/de.json'
import en from './locales/en.json'
import es from './locales/es.json'
import fr from './locales/fr.json'
import it from './locales/it.json'
import nl from './locales/nl.json'
import pt from './locales/pt.json'
import ru from './locales/ru.json'
import zh from './locales/zh.json'

// Code → display label + RTL flag (Arabic is RTL, the rest are LTR).
export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English', dir: 'ltr' },
  { code: 'ar', label: 'العربية', dir: 'rtl' },
  { code: 'bn', label: 'বাংলা', dir: 'ltr' },
  { code: 'de', label: 'Deutsch', dir: 'ltr' },
  { code: 'es', label: 'Español', dir: 'ltr' },
  { code: 'it', label: 'Italiano', dir: 'ltr' },
  { code: 'pt', label: 'Português', dir: 'ltr' },
  { code: 'fr', label: 'Français', dir: 'ltr' },
  { code: 'nl', label: 'Nederlands', dir: 'ltr' },
  { code: 'ru', label: 'Русский', dir: 'ltr' },
  { code: 'zh', label: '中文', dir: 'ltr' },
]

// TEMP: the preferred language comes from a constant until profile prefs land.
export const DEFAULT_LANGUAGE = 'en'

const DICTIONARIES = { en, ar, bn, de, es, it, pt, fr, nl, ru, zh }

const LanguageContext = createContext({
  lang: DEFAULT_LANGUAGE,
  dir: 'ltr',
  setLang: () => {},
  t: (text) => text,
})

// Replace `{{name}}` placeholders with values from `vars`.
function interpolate(template, vars) {
  if (!vars) return template
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) =>
    vars[key] !== undefined && vars[key] !== null ? String(vars[key]) : `{{${key}}}`
  )
}

function getDirection(code) {
  const lang = SUPPORTED_LANGUAGES.find((l) => l.code === code)
  return lang ? lang.dir : 'ltr'
}

// Read the active language from the cached global-settings snapshot. Falls
// back to the constant default so the provider can boot before
// ProductSelection has had a chance to populate the cache.
function readInitialLang(initialLang) {
  const cached =
    typeof window !== 'undefined' ? globalSettingsStorage.getCached()?.defaultLanguage : null
  if (cached && DICTIONARIES[cached]) return cached
  return initialLang || DEFAULT_LANGUAGE
}

export function LanguageProvider({ children, initialLang }) {
  const [lang, setLangState] = useState(() => readInitialLang(initialLang))

  const setLang = useCallback(
    (next) => {
      if (!DICTIONARIES[next] || next === lang) return
      const previous = lang
      // Optimistic: flip UI + cache immediately so the dropdown reflects the
      // choice without waiting for the round-trip. Reverted in `catch` if
      // the server rejects the change.
      setLangState(next)
      globalSettingsStorage.setDefaultLanguage(next)
      updateGlobalSettings({ defaultLanguage: next })
        .then(() => {
          // Keep the Global Settings page in sync — its React Query cache
          // is scoped to 60s, so without invalidation the form would show
          // a stale language for up to a minute after a header switch.
          queryClient.invalidateQueries({ queryKey: ['global-settings'] })
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.warn('[LanguageContext] failed to persist language', err)
          setLangState(previous)
          globalSettingsStorage.setDefaultLanguage(previous)
        })
    },
    [lang]
  )

  // Apply direction + lang on <html> for proper bidi handling.
  useEffect(() => {
    if (typeof document === 'undefined') return
    const dir = getDirection(lang)
    document.documentElement.setAttribute('lang', lang)
    document.documentElement.setAttribute('dir', dir)
  }, [lang])

  const t = useCallback(
    (text, vars) => {
      if (text === undefined || text === null) return ''
      const source = typeof text === 'string' ? text : String(text)
      const dict = DICTIONARIES[lang] || DICTIONARIES[DEFAULT_LANGUAGE]
      const fromActive = dict[source]
      const translation = fromActive !== undefined ? fromActive : DICTIONARIES.en[source]
      return interpolate(translation !== undefined ? translation : source, vars)
    },
    [lang]
  )

  const value = useMemo(() => ({ lang, dir: getDirection(lang), setLang, t }), [lang, setLang, t])

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export function useLanguage() {
  return useContext(LanguageContext)
}

// Convenience: `t('Hello {{name}}', { name: 'Sara' })` returns translated + interpolated.
export function translate(text, vars) {
  // Standalone helper for code paths that don't sit inside a component
  // (e.g. error toasts inside mutation callbacks). Falls back to source.
  if (text === undefined || text === null) return ''
  const source = typeof text === 'string' ? text : String(text)
  let lang = DEFAULT_LANGUAGE
  if (typeof window !== 'undefined') {
    const cached = globalSettingsStorage.getCached()?.defaultLanguage
    if (cached && DICTIONARIES[cached]) lang = cached
  }
  const dict = DICTIONARIES[lang] || DICTIONARIES[DEFAULT_LANGUAGE]
  const fromActive = dict[source]
  const translation = fromActive !== undefined ? fromActive : DICTIONARIES.en[source]
  return interpolate(translation !== undefined ? translation : source, vars)
}
